<template>
  <div class="month-day-list">
    <!-- Header with month name and batch order button -->
    <div class="month-header">
      <div class="month-name">
        {{ monthName }}
      </div>
      <!-- <button
        v-if="orders && offerCalendar"
        class="batch-button"
        @click="showBatchOrderModal"
      >
        <span class="batch-button-text">Zamów zbiorczo</span>
      </button> -->
    </div>

    <!-- Loading state -->
    <div
      v-if="pending"
      class="loading"
    >
      <i class="fas fa-spinner fa-spin" />
      <span>Ładowanie...</span>
    </div>

    <!-- Days list -->
    <div
      v-else-if="orders && offerCalendar"
      class="days-container"
    >
      <!-- Back button when scrolling up -->
      <div
        v-if="showTopButton"
        class="back-button-container"
      >
        <button
          class="back-button"
          @click="goBack"
        >
          <i class="fas fa-chevron-up" />
          <span>Cofnij</span>
        </button>
      </div>

      <!-- Days -->
      <div
        v-for="day in days"
        :key="day.format('YYYY-MM-DD')"
        :ref="`day_${day.format('YYYY-MM-DD')}`"
        class="day-row"
      >
        <OrderDayRow
          :day="day"
          :orders="orders"
          :payer="payer"
          :catalog="catalog"
          :offer-calendar="offerCalendar"
          @day-click="triggerDayModal"
        />
      </div>

      <!-- Next month preview -->
      <div class="next-month">
        <div class="next-month-name">
          {{ nextMonthName }}
        </div>
        <div class="next-month-button-container">
          <button
            class="next-month-button"
            @click="goToNextMonth"
          >
            Pokaż
          </button>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="empty-state"
    >
      <i class="fas fa-calendar" />
      <span>Brak dostępnych ofert</span>
    </div>

    <!-- Day Order Modal -->
    <DayOrderModal
      v-if="modalData"
      :visible.sync="showDayModal"
      :day="modalData.day"
      :month-end="modalData.monthEnd"
      :batch="modalData.batch"
      :payer="modalData.payer"
      :catalog="catalog"
      :offer="modalData.offer"
      :orders="modalData.orders"
      @close="handleModalClose"
      @day-change="handleDayChange"
    />
  </div>
</template>

<script>
import axios from '@/api/axios';
import moment from 'moment';
import { mapGetters } from 'vuex';
import DayOrderModal from './DayOrderModal';
import OrderDayRow from './OrderDayRow';

export default {
  name: 'MonthDayList',
  props: {
    catalogId: {
      type: String,
      required: true,
    },
    payeeId: {
      type: String,
      required: true,
    },
    payerId: {
      type: String,
      required: true,
    },
    catalog: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      orders: null,
      offerCalendar: null,
      from: moment().startOf('month'),
      pending: false,
      showTopButton: false,
      todayPosition: null,
      isInitialized: false,
      pendingRefresh: false,
      showDayModal: false,
      modalData: null,
    };
  },
  components: {
    OrderDayRow,
    DayOrderModal,
  },
  computed: {
    ...mapGetters([
      'userPayers',
      'userCatalogs',
      'ordersRefresh']),

    payer() {
      return this.userPayers.find((p) => p.payerId === this.payerId);
    },

    monthEnd() {
      return moment(this.from).add(1, 'month').startOf('month');
    },

    monthName() {
      return this.from.format('MMMM YYYY');
    },

    nextMonthName() {
      return moment(this.from).add(1, 'month').format('MMMM YYYY');
    },

    days() {
      const days = [];
      const current = moment(this.from);

      while (current.isBefore(this.monthEnd)) {
        days.push(moment(current));
        current.add(1, 'day');
      }

      return days;
    },

    needsOrderRefresh() {
      // Check if orders need refresh based on store state
      return this.ordersRefresh.some(
        (x) => x.payerId === this.payerId && x.catalogId === this.catalogId,
      );
    },
  },
  watch: {
    from() {
      this.refresh();
    },

    needsOrderRefresh(newVal) {
      if (newVal && this.isInitialized) {
        this.refresh();
      }
    },
  },

  methods: {
    async refresh() {
      if (this.pendingRefresh) return;

      this.pendingRefresh = true;
      this.pending = true;

      try {
        const [ordersResponse, calendarResponse] = await Promise.all([
          axios.get('/user/shop/orders', {
            params: {
              query: {
                payerId: this.payerId,
                from: this.from.format('YYYY-MM-DD'),
                to: this.monthEnd.format('YYYY-MM-DD'),
                catalogId: this.catalogId,
              },
            },
          }),
          axios.get('/v2/user/shop/offer-calendar', {
            params: {
              query: {
                payerId: this.payerId,
                from: this.from.format('YYYY-MM-DD'),
                to: this.monthEnd.format('YYYY-MM-DD'),
                catalogId: this.catalogId,
              },
            },
          }),
        ]);

        this.orders = ordersResponse.data;
        this.offerCalendar = calendarResponse.data;

        // Mark orders as refreshed
        this.$store.commit('ordersRefreshed', {
          payerId: this.payerId,
          catalogId: this.catalogId,
        });

        // Scroll to today if it's the current month
        this.$nextTick(() => {
          this.scrollToToday();
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to refresh data:', error);
      } finally {
        this.pending = false;
        this.pendingRefresh = false;
      }
    },

    goBack() {
      if (this.from.isSame(moment().startOf('month'))) {
        this.from = moment(this.from).subtract(1, 'month').startOf('month');
      } else {
        this.from = moment(this.from).startOf('month');
      }
    },

    goToNextMonth() {
      this.from = moment(this.from).add(1, 'month').startOf('month');
      this.scrollToTop();
    },

    handleScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      this.showTopButton = scrollTop < -20;
    },

    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },

    scrollToToday() {
      if (this.from.isSame(moment().startOf('month')) && this.todayPosition !== null) {
        const today = moment().startOf('day');
        const todayElement = this.$refs[`day_${today.format('YYYY-MM-DD')}`];

        if (todayElement && todayElement[0]) {
          const elementTop = todayElement[0].offsetTop;
          window.scrollTo({
            top: elementTop - 50,
            behavior: 'smooth',
          });
        }
      }
    },

    triggerDayModal(day) {
      this.modalData = {
        day: day.format('YYYY-MM-DD'),
        payer: this.payer,
        catalog: this.catalog,
        offer: this.offerCalendar,
        orders: this.orders,
        batch: false,
      };
      this.showDayModal = true;
    },

    showBatchOrderModal() {
      this.modalData = {
        day: this.from.format('YYYY-MM-DD'),
        monthEnd: this.monthEnd.format('YYYY-MM-DD'),
        payer: this.payer,
        catalog: this.catalog,
        offer: this.offerCalendar,
        orders: this.orders,
        batch: true,
      };
      this.showDayModal = true;
    },

    handleModalClose() {
      this.showDayModal = false;
      this.modalData = null;
    },

    handleDayChange(data) {
      this.modalData = data;
    },
  },
  mounted() {
    this.refresh();
    this.isInitialized = true;

    this.$root.$on('order-created', this.refresh);

    // Set up scroll listener
    window.addEventListener('scroll', this.handleScroll);
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    this.$root.$off('order-created', this.refresh);
  },
};
</script>

<style lang="scss" scoped>
.month-day-list {
  min-height: 100vh;
  margin-left: -15px;
}

.month-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background-color: #fafafa;
  border-bottom: 1px solid #e0e0e0;
}

.month-name {
  font-size: 18px;
  font-weight: 500;
  text-transform: capitalize;
  margin-left: 5px;
}

.batch-button {
  background: none;
  border: none;
  padding: 5px 10px;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: #e0e0e0;
  }
}

.batch-button-text {
  color: #007bff;
  font-size: 12px;
  text-decoration: underline;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px;
  color: #666;

  i {
    font-size: 24px;
    margin-bottom: 10px;
  }
}

.days-container {
  padding: 15px 0;
}

.back-button-container {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.back-button {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 5px 15px;
  border-radius: 5px;
  cursor: pointer;
  color: #aaa;
  font-size: 12px;

  i {
    margin-right: 5px;
    font-size: 15px;
  }

  &:hover {
    background-color: #e0e0e0;
  }
}

.day-row {
  margin-bottom: 10px;
}

.next-month {
  padding: 10px;

  .next-month-name {
    font-size: 18px;
    font-weight: 500;
    text-transform: capitalize;
    margin-left: 5px;
  }

  .next-month-button-container {
    display: flex;
    justify-content: center;
    margin: 10px;
    gap: 10px;
  }

  .next-month-button {
    background-color: #007bff;
    color: white;
    border: none;
    padding: 5px 10px;
    border-radius: 5px;
    width: 150px;
    margin-bottom: 10px;
    cursor: pointer;

    &:hover {
      background-color: #0056b3;
    }
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px;
  color: #666;

  i {
    font-size: 48px;
    margin-bottom: 20px;
    opacity: 0.5;
  }
}
</style>
